import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, Star, ChevronLeft, ChevronRight, Trash2, Loader2, Link2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIDE = 1600;

// Resize + re-encode in the browser so uploads stay small and fast.
const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL('image/jpeg', 0.88));
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read image')); };
  img.src = url;
});

const ImageTile = ({ src, index, total, onMain, onMove, onRemove }) => (
  <div className="relative group rounded-lg border border-slate-200 bg-white overflow-hidden" data-testid={`product-image-tile-${index}`}>
    <img src={src} alt={`Product image ${index + 1}`} className="w-full aspect-square object-cover" />
    {index === 0 && (
      <span className="absolute top-1 left-1 bg-emerald-700 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded" data-testid="product-image-main-badge">Main</span>
    )}
    <div className="absolute inset-x-0 bottom-0 flex justify-between bg-black/60 p-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
      <button type="button" disabled={index === 0} onClick={() => onMove(index, -1)} className="p-1 text-white disabled:opacity-30" title="Move left" data-testid={`product-image-left-${index}`}><ChevronLeft className="w-3.5 h-3.5" /></button>
      <button type="button" disabled={index === 0} onClick={() => onMain(index)} className="p-1 text-white disabled:opacity-30" title="Make main image" data-testid={`product-image-main-${index}`}><Star className="w-3.5 h-3.5" /></button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(index, 1)} className="p-1 text-white disabled:opacity-30" title="Move right" data-testid={`product-image-right-${index}`}><ChevronRight className="w-3.5 h-3.5" /></button>
      <button type="button" onClick={() => onRemove(index)} className="p-1 text-red-300" title="Remove" data-testid={`product-image-remove-${index}`}><Trash2 className="w-3.5 h-3.5" /></button>
    </div>
  </div>
);

export const ProductImageManager = ({ images, onChange }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const handleFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const added = [];
    for (const file of Array.from(files)) {
      try {
        const data = await fileToDataUrl(file);
        const r = await axios.post(`${API}/admin/product-images`, { data, mime: 'image/jpeg' });
        added.push(r.data.url);
      } catch (e) {
        toast.error(`${file.name}: ${e.response?.data?.detail || 'upload failed'}`);
      }
    }
    if (added.length) {
      onChange([...images, ...added]);
      toast.success(`${added.length} image${added.length === 1 ? '' : 's'} added — click Save to publish`);
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const addUrl = () => {
    const u = urlInput.trim();
    if (!u) return;
    if (!/^(https?:\/\/|\/)/.test(u)) { toast.error('Enter a full https:// link'); return; }
    onChange([...images, u]);
    setUrlInput('');
  };

  const move = (i, dir) => {
    const next = [...images];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    onChange(next);
  };
  const makeMain = (i) => onChange([images[i], ...images.filter((_, j) => j !== i)]);
  const remove = (i) => onChange(images.filter((_, j) => j !== i));

  return (
    <div data-testid="product-image-manager">
      <label className="block text-xs font-medium text-slate-700 mb-1">Product images <span className="text-slate-400 font-normal">(first image is the main one)</span></label>
      {images.length > 0 && (
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 mb-2">
          {images.map((src, i) => (
            <ImageTile key={`${src}-${i}`} src={src} index={i} total={images.length} onMain={makeMain} onMove={move} onRemove={remove} />
          ))}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-2">
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="product-image-file-input" />
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-emerald-700 text-white text-sm font-medium hover:bg-emerald-800 disabled:opacity-60" data-testid="product-image-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading…' : 'Upload images'}
        </button>
        <div className="flex flex-1 gap-2">
          <input type="url" value={urlInput} onChange={(e) => setUrlInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addUrl(); } }} placeholder="…or paste an image link" className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600/30" data-testid="product-image-url-input" />
          <button type="button" onClick={addUrl} className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50" data-testid="product-image-url-add-btn"><Link2 className="w-4 h-4" />Add</button>
        </div>
      </div>
      <p className="text-[11px] text-slate-500 mt-1">JPG, PNG or WebP · up to 8 MB each · large photos are resized automatically.</p>
    </div>
  );
};
